import { Checkbox } from '@carbon/react';
import { useAtom } from 'jotai';
import { MapUserConfigAtom } from '../types';
import style from '../map.module.scss';
import { CollapsibleSection } from '../collapsible-section';

export const DrillingRigSelector = ({
  mapConfigAtom,
}: {
  mapConfigAtom: MapUserConfigAtom;
}) => {
  const [mapConfig, setMapConfig] = useAtom(mapConfigAtom);

  // Define our categories and status filters
  const categories = {
    horizontal: {
      label: 'Horizontal Rigs',
      key: 'horizontal' as const,
    },
    vertical: {
      label: 'Vertical/Directional Rigs',
      key: 'vertical' as const,
    },
  };

  // Status filters that will appear under each category with their colors
  const statusFilters = [
    { key: 'drilling', label: 'Drilling' },
    { key: 'down', label: 'Down' },
    { key: 'notReporting', label: 'Not Reporting' },
    { key: 'notTracking', label: 'Not Tracking' },
    { key: 'unreportedStatus', label: 'Unreported Status' },
  ];

  // Helper function to determine checkbox state for categories
  const getCategoryCheckState = (
    categoryKey: 'horizontal' | 'vertical'
  ): { checked: boolean; indeterminate: boolean } => {
    const filters = statusFilters;

    // Get all child filter keys for this category
    const childFilters = filters.map((status) => {
      return status.key;
    });

    // Check if all child filters are checked
    const allChildrenChecked = childFilters.every((filter) => {
      const optionKey = `${categoryKey}${
        filter.charAt(0).toUpperCase() + filter.slice(1)
      }` as keyof typeof mapConfig.drillingRigs.options;
      return mapConfig.drillingRigs?.options?.[optionKey] === true;
    });

    // Check if some but not all child filters are checked
    const someChildrenChecked = childFilters.some((filter) => {
      const optionKey = `${categoryKey}${
        filter.charAt(0).toUpperCase() + filter.slice(1)
      }` as keyof typeof mapConfig.drillingRigs.options;
      return mapConfig.drillingRigs?.options?.[optionKey] === true;
    });

    return {
      checked: allChildrenChecked,
      indeterminate: !allChildrenChecked && someChildrenChecked,
    };
  };

  const handleCategoryChange = (
    categoryKey: 'horizontal' | 'vertical',
    checked: boolean
  ) =>
    setMapConfig((current) => {
      const filters = statusFilters;

      // Create a new config object with flat structure
      const newConfig = {
        ...current,
        drillingRigs: {
          ...current.drillingRigs,
          checked: current.drillingRigs?.checked || false,
          options: {
            ...current.drillingRigs?.options,
          },
        },
      };

      // If checking a category, check all its child filters
      if (checked) {
        // Check all child filters for this category
        filters.forEach((status) => {
          const optionKey = `${categoryKey}${
            status.key.charAt(0).toUpperCase() + status.key.slice(1)
          }` as keyof typeof newConfig.drillingRigs.options;
          newConfig.drillingRigs.options[optionKey] = true;
        });
      } else {
        // If unchecking a category, also uncheck all its child filters
        filters.forEach((status) => {
          const optionKey = `${categoryKey}${
            status.key.charAt(0).toUpperCase() + status.key.slice(1)
          }` as keyof typeof newConfig.drillingRigs.options;
          newConfig.drillingRigs.options[optionKey] = false;
        });
      }

      // Check if any option is checked to determine overall layer visibility
      const anyOptionChecked = Object.values(
        newConfig.drillingRigs.options
      ).some((value) => value);
      newConfig.drillingRigs.checked = anyOptionChecked;

      return newConfig;
    });

  const handleStatusChange = (
    categoryKey: 'horizontal' | 'vertical',
    statusKey: string,
    checked: boolean
  ) =>
    setMapConfig((current) => {
      const newConfig = {
        ...current,
        drillingRigs: {
          ...current.drillingRigs,
          checked: current.drillingRigs?.checked || false,
          options: {
            ...current.drillingRigs?.options,
          },
        },
      };

      // Update the specific status
      const optionKey = `${categoryKey}${
        statusKey.charAt(0).toUpperCase() + statusKey.slice(1)
      }` as keyof typeof newConfig.drillingRigs.options;
      newConfig.drillingRigs.options[optionKey] = checked;

      // Check if any option is checked to determine overall layer visibility
      const anyOptionChecked = Object.values(
        newConfig.drillingRigs.options
      ).some((value) => value);
      newConfig.drillingRigs.checked = anyOptionChecked;

      return newConfig;
    });

  return (
    <div className={style.layersSelector}>
      {Object.values(categories).map((category) => {
        const filters = statusFilters;
        const categoryState = getCategoryCheckState(category.key);

        return (
          <CollapsibleSection
            key={category.key}
            id={`drilling-rig-${category.key}`}
            title={category.label}
            className={style.drillingRigCategorySection}
            checked={categoryState.checked}
            indeterminate={categoryState.indeterminate}
            onChange={(_evt, { checked }: { checked: boolean }) =>
              handleCategoryChange(category.key, checked)
            }
            defaultOpen={true}
          >
            <div className={style.groupChecks}>
              {filters.map((status) => {
                const pascal = (s: string) =>
                  s.charAt(0).toUpperCase() + s.slice(1);
                const drillingRigKey = `drillingRig${pascal(
                  category.key
                )}${pascal(status.key)}`;
                const isChecked = (() => {
                  const optionKey = `${category.key}${
                    status.key.charAt(0).toUpperCase() + status.key.slice(1)
                  }` as keyof typeof mapConfig.drillingRigs.options;
                  return mapConfig.drillingRigs?.options?.[optionKey] || false;
                })();

                return (
                  <div key={status.key} className={style[drillingRigKey]}>
                    <div className={style.checkboxContainer}>
                      <Checkbox
                        id={drillingRigKey}
                        labelText={status.label}
                        className={style.drillingRigStatusOption}
                        checked={isChecked}
                        onChange={(_evt, { checked }: { checked: boolean }) =>
                          handleStatusChange(category.key, status.key, checked)
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleSection>
        );
      })}
    </div>
  );
};
